import {
  EyeOutlined,
  RobotOutlined,
  SaveOutlined
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Row,
  Space,
  Typography
} from 'antd';
import parse from 'html-react-parser';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../helpers/axiosConfig';
import {
  clearOnePetitionState,
  initializeCreatePetition,
  resetCreatePetition,
  setCreatePetitionErrors,
  setCreatePetitionField,
  setCreatePetitionImages
} from '../../../store/petition/petitionSlice';
import MultipleImageUpload from '../../ui/MultipleImageUpload/MultipleImageUpload';
import TextToSpeech from '../../voice/TextToSpeech/TextToSpeech';
import VoiceButton from '../../voice/VoiceButton/VoiceButton';
import VoiceInputField from '../../voice/VoiceInputField/VoiceInputField';
import './EnhancedCreatePetition.css';

const { Title, Text } = Typography;

const EnhancedCreatePetition = () => {
  const { t } = useTranslation('global');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { loading, createPetition } = useSelector((state) => state.petitions);
  const petition = createPetition;
  const errors = createPetition.errors;
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  
  // Инициализация состояния при монтировании
  useEffect(() => {
    const generatedContent = location.state?.generatedContent;
    const transcript = location.state?.transcript;
    const clientId = location.state?.clientId;
    const moderation = location.state?.moderation;
    const initialImages = location.state?.images || [];

    // Инициализируем из location.state если есть данные
    // location.state имеет приоритет над сохраненными данными в Redux
    if (generatedContent || transcript || clientId) {
      dispatch(initializeCreatePetition({
        generatedContent,
        transcript,
        clientId,
        moderation,
        images: initialImages
      }));
    } 
    // Если location.state пустой, но в Redux уже есть данные (из предыдущей навигации),
    // то используем данные из Redux (они уже установлены в petition через useSelector)

    // Очищаем при размонтировании
    return () => {
      dispatch(clearOnePetitionState());
      // Не сбрасываем createPetition, чтобы сохранить данные при навигации
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleInputChange = (field, value) => {
    dispatch(setCreatePetitionField({ field, value }));
  };

  const handleImagesChange = (newImages) => {
    dispatch(setCreatePetitionImages(newImages));
  };

  const validateForm = () => {
    const newErrors = {
      title: !petition.title.trim(),
      description: !petition.description.trim(),
      images: !petition.images || petition.images.length === 0
    };
    
    dispatch(setCreatePetitionErrors(newErrors));
    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const errorMessages = [];
      if (errors.title) errorMessages.push('Заголовок обязателен');
      if (errors.description) errorMessages.push('Описание обязательно');
      if (errors.images) errorMessages.push(t('createPetition.imageRequired'));
      message.error(`Пожалуйста, заполните все обязательные поля: ${errorMessages.join(', ')}`);
      return;
    }


    // Проверяем наличие изображений
    if (!petition.images || petition.images.length === 0) {
      message.error(t('createPetition.imageRequired'));
      dispatch(setCreatePetitionErrors({ images: true }));
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', petition.title);
      formData.append('description', petition.description);
      
      // Добавляем все изображения
      petition.images.forEach((image) => {
        formData.append('images', image);
      });
      
      if (petition.client_id) {
        formData.append('client_id', petition.client_id);
      }
      
      if (petition.voice_transcript) {
        formData.append('voice_transcript', petition.voice_transcript);
      }
      
      if (petition.is_generated_by_ai !== undefined) {
        formData.append('is_generated_by_ai', petition.is_generated_by_ai);
      }

      
      // Используем прямой вызов API для voice-petition endpoint
      const response = await api.post('/voice-petition/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      
      message.success({
        content: `✅ ${t('moderation.petitionSent')}`,
        duration: 5,
      });
      
      // Сбрасываем состояние создания инициативы после успешного создания
      dispatch(resetCreatePetition());
      
      // Переходим на страницу созданной инициативы
      setTimeout(() => {
        navigate(`/petition/${response.data.id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error creating petition:', error);
      
      if (error.response?.status === 429) {
        const retryMinutes = Math.ceil(error.response.data.retry_after / 60);
        message.error(`Превышен лимит создания инициатив. Попробуйте через ${retryMinutes} минут.`);
      } else if (error.response?.data) {
        const errors = error.response.data;
        let errorMessages = [];
        
        Object.keys(errors).forEach(field => {
          const fieldErrors = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          const fieldName = {
            'images': 'Изображения',
            'title': 'Заголовок',
            'description': 'Описание',
            'error': 'Ошибка'
          }[field] || field;
          
          fieldErrors.forEach(err => {
            errorMessages.push(`${fieldName}: ${err}`);
          });
        });
        
        message.error({
          content: (
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Ошибка создания инициативы:</div>
              {errorMessages.map((msg, index) => (
                <div key={index} style={{ marginBottom: 4 }}>• {msg}</div>
              ))}
            </div>
          ),
          duration: 8,
        });
      } else {
        message.error('Не удалось создать инициативу. Проверьте подключение к интернету.');
      }
    }
    
    // Старая логика fallback удалена, так как всегда используем API
  };

  const handlePreview = () => {
    if (!validateForm()) {
      const errorMessages = [];
      if (errors.title) errorMessages.push('Заголовок обязателен');
      if (errors.description) errorMessages.push('Описание обязательно');
      if (errors.images) errorMessages.push(t('createPetition.imageRequired'));
      message.error(`Пожалуйста, заполните все обязательные поля: ${errorMessages.join(', ')}`);
      return;
    }
    
    // Проверяем наличие изображений
    if (!petition.images || petition.images.length === 0) {
      message.error(t('createPetition.imageRequiredPreview'));
      dispatch(setCreatePetitionErrors({ images: true }));
      return;
    }
    
    // Открываем модальное окно предпросмотра (БЕЗ создания петиции)
    setIsPreviewModalVisible(true);
  };

  return (
    <div className="enhanced-create-petition">
      <div className="container">
        <div className="petition-header">
          <Title level={1}>
            {petition.generatedContent ? t('voice.editGenerated') : t('voice.createNew')}
          </Title>
          
          {petition.generatedContent && (
            <Alert
              message={t('createPetition.aiGenerated')}
              description={t('voice.voiceBasedDescription')}
              type="info"
              icon={<RobotOutlined />}
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          {petition.moderation && !petition.moderation.is_safe && (
            <Alert
              message="Внимание при модерации"
              description={`Обнаружены потенциальные проблемы: ${petition.moderation.issues.join(', ')}`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={24}>
            <Card title={t('voice.basicInfo')} className="petition-card">
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                
                <div className="form-field">
                  <label className="field-label">
                    {t('voice.initiativeTitle')} <span className="required">*</span>
                  </label>
                  <Text type="secondary" className="field-hint">
                    {t('voice.titleHint')}
                  </Text>
                  <div style={{ position: 'relative' }}>
                    <Input
                      placeholder={t('voice.titlePlaceholder')}
                      value={petition.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      status={errors.title ? 'error' : ''}
                      maxLength={200}
                      showCount
                      size="large"
                      style={{ paddingRight: 40 }}
                    />
                    <div style={{ 
                      position: 'absolute', 
                      right: 8, 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      zIndex: 1
                    }}>

                      <VoiceButton
                        onTranscript={(transcript) => {
                          // Заменяем весь текст новым голосовым вводом
                          handleInputChange('title', transcript.slice(0, 200));
                        }}
                        maxLength={200}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-field">
                  <label className="field-label">
                    {t('voice.initiativeDescription')} <span className="required">*</span>
                  </label>
                  <Text type="secondary" className="field-hint">
                    {t('voice.descriptionHint')}
                  </Text>
                  <VoiceInputField
                    value={petition.description}
                    onChange={(value) => handleInputChange('description', value)}
                    placeholder={t('voice.descriptionPlaceholder')}
                    maxLength={5000}
                  />
                  {errors.description && (
                    <Text type="danger">{t('voice.descriptionRequired')}</Text>
                  )}
                </div>

                <div className="form-field">
                  <label className="field-label">
                    Изображения <span className="required">*</span>
                  </label>
                  <Text type="secondary" className="field-hint">
                    {t('createPetition.addImages')}
                  </Text>
                  <MultipleImageUpload
                    images={petition.images}
                    onImagesChange={(newImages) => {
                      handleImagesChange(newImages);
                    }}
                    maxImages={10}
                    maxSizeMB={2}
                    acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                  />
                  {errors.images && (
                    <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                      {t('createPetition.imageRequired')}
                    </Text>
                  )}
                </div>

              </Space>
            </Card>
          </Col>
        </Row>

        <div className="form-actions">
          <Space size="middle">
            <TextToSpeech 
              text={petition.description}
              title={petition.title}
              size="large"
            />
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={handlePreview}
              size="large"
            >
              {t('voice.preview')}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={loading}
              size="large"
            >
              {t('voice.publishInitiative')}
            </Button>
          </Space>
        </div>
      </div>

      {/* Модальное окно предпросмотра */}
      <Modal
        title={t('voice.previewInitiative')}
        open={isPreviewModalVisible}
        onCancel={() => setIsPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsPreviewModalVisible(false)}>
            {t('voice.close')}
          </Button>,
          <Button
            key="publish"
            type="primary"
            onClick={() => {
              setIsPreviewModalVisible(false);
              handleSubmit();
            }}
            loading={loading}
          >
            {t('voice.publish')}
          </Button>
        ]}
        width={1200}
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {/* Заголовок */}
          <Card style={{ marginBottom: 16 }}>
            <Title level={2}>{petition.title}</Title>
            <Space size="small" style={{ marginTop: 8 }}>
              <Text type="secondary">{t('voice.statusLabel')} </Text>
              <Typography.Text type="secondary">{t('voice.draft')}</Typography.Text>
            </Space>
          </Card>

          {/* Описание */}
          <Card title={t('voice.initiativeDescription')} style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 16, lineHeight: 1.6 }}>
              {typeof petition.description === 'string' ? 
                parse(petition.description.replace(/(\r\n|\n|\r)/g, '<br>')) : 
                petition.description
              }
            </div>
          </Card>

          {/* Изображения */}
          {petition.images && petition.images.length > 0 && (
            <Card title="Изображения инициативы" style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                {petition.images.map((img, index) => {
                  // Если это File объект, создаем blob URL
                  const imageUrl = img instanceof File ? URL.createObjectURL(img) : img;
                  return (
                    <div key={index} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                      <img
                        src={imageUrl}
                        alt={`Изображение ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: 8,
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          // Можно открыть в полном размере
                          window.open(imageUrl, '_blank');
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        padding: '4px 8px',
                        fontSize: '12px',
                        textAlign: 'center'
                      }}>
                        Изображение {index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EnhancedCreatePetition;